import { FileUp, FolderOpen, Send, Square, X } from 'lucide-react';
import { useRef } from 'react';
import type { AttachmentRef } from '../api';
import { formatBytes } from '../utils';

interface ComposerProps {
  value: string;
  attachments: AttachmentRef[];
  disabled: boolean;
  running: boolean;
  uploading: boolean;
  error?: string;
  onChange(value: string): void;
  onOpenWorkspace(): void;
  onUpload(files: File[]): void;
  onRemoveAttachment(index: number): void;
  onSubmit(): void;
  onStop(): void;
}

export function Composer({
  value,
  attachments,
  disabled,
  running,
  uploading,
  error,
  onChange,
  onOpenWorkspace,
  onUpload,
  onRemoveAttachment,
  onSubmit,
  onStop,
}: ComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSend = !disabled && !running && Boolean(value.trim() || attachments.length);

  return (
    <div className="composer-shell">
      {attachments.length > 0 && (
        <div className="attachment-list" aria-label="已附加文件">
          {attachments.map((attachment, index) => (
            <span className="attachment-chip" key={`${attachment.id ?? attachment.path ?? attachment.name}-${index}`}>
              <span>
                {attachment.name}
                <small>{attachment.kind === 'upload' ? '已上传' : '工作区'}</small>
              </span>
              {attachment.size !== undefined && <span className="chip-size">{formatBytes(attachment.size)}</span>}
              <button
                type="button"
                onClick={() => onRemoveAttachment(index)}
                aria-label={`移除附件 ${attachment.name}`}
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (canSend) onSubmit();
          }
        }}
        disabled={disabled}
        placeholder={disabled ? '请先新建或选择会话' : '描述研究问题、希望核查的证据或要生成的报告…'}
        aria-label="研究请求"
        rows={3}
      />

      <div className="composer-footer">
        <div className="composer-sources">
          <button className="secondary-button" type="button" onClick={onOpenWorkspace} disabled={disabled}>
            <FolderOpen size={16} />
            浏览工作区
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
          >
            <FileUp size={16} />
            {uploading ? '正在上传' : '上传文件'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              if (files.length) onUpload(files);
              event.target.value = '';
            }}
          />
        </div>

        {running ? (
          <button className="danger-button" type="button" onClick={onStop}>
            <Square size={14} fill="currentColor" />
            停止
          </button>
        ) : (
          <button className="primary-button" type="button" onClick={onSubmit} disabled={!canSend}>
            <Send size={16} />
            发送
          </button>
        )}
      </div>
      {error && <div className="composer-error" role="alert">{error}</div>}
    </div>
  );
}
